'use client';

import { motion } from "framer-motion";
import Link from "next/link";
import { useParams } from "next/navigation";
import { localizeSpelling } from "@/lib/spelling-localization";
import { 
  Brain, 
  Database, 
  Cloud, 
  GitBranch, 
  Cpu, 
  BarChart3, 
  Shield, 
  Network,
  TrendingUp
} from "lucide-react";

// Modern gradient color palette for each technology
const baseTechnologies = [
  {
    name: "Machine Learning",
    description: "Advanced AI Algorithms",
    icon: Brain,
    gradient: "from-purple-600 via-blue-600 to-cyan-500",
    glowColor: "shadow-purple-500/25",
    solutions: ["Supervised Learning", "Unsupervised Learning", "Reinforcement Learning"],
    href: "/technologies/machine-learning",
    isWide: true
  },
  {
    name: "Big Data",
    description: "Data Processing & Analytics", 
    icon: Database,
    gradient: "from-amber-500 via-orange-500 to-red-500",
    glowColor: "shadow-orange-500/25",
    solutions: ["Hadoop", "Spark", "Real-time Processing"],
    href: "/technologies/big-data",
    isWide: false
  },
  {
    name: "Cloud Computing",
    description: "Scalable Infrastructure",
    icon: Cloud,
    gradient: "from-blue-500 via-cyan-500 to-teal-500",
    glowColor: "shadow-blue-500/25",
    solutions: ["AWS", "Azure", "Google Cloud"],
    href: "/technologies/cloud-computing",
    isWide: false
  },
  {
    name: "DevOps",
    description: "Development Operations",
    icon: GitBranch,
    gradient: "from-green-500 via-emerald-500 to-teal-600",
    glowColor: "shadow-green-500/25",
    solutions: ["CI/CD Pipelines", "Docker", "Kubernetes"],
    href: "/technologies/devops",
    isWide: true
  },
  {
    name: "Edge Computing",
    description: "Distributed Intelligence",
    icon: Cpu,
    gradient: "from-indigo-500 via-purple-500 to-pink-500",
    glowColor: "shadow-indigo-500/25",
    solutions: ["IoT Integration", "Real-time Processing", "Low Latency Systems"],
    href: "/technologies/edge-computing",
    isWide: true
  },
  {
    name: "APIs & Integration",
    description: "System Interconnections",
    icon: Network,
    gradient: "from-rose-500 via-pink-500 to-purple-500",
    glowColor: "shadow-rose-500/25",
    solutions: ["RESTful APIs", "GraphQL", "Microservices"],
    href: "/technologies/apis-integration",
    isWide: false
  },
  {
    name: "Cybersecurity",
    description: "Data & System Protection",
    icon: Shield,
    gradient: "from-slate-700 via-gray-700 to-zinc-700",
    glowColor: "shadow-slate-500/25",
    solutions: ["Threat Detection", "Encryption", "Compliance Management"],
    href: "/technologies/cybersecurity",
    isWide: true
  }
];

// Create a special technology item for Data Visualization with properly localized capabilities
const createDataVisualizationTech = (locale: string) => {
  return {
    name: "Data Visualization",
    description: "Visual Analytics",
    icon: BarChart3,
    gradient: "from-yellow-400 via-orange-500 to-red-500",
    glowColor: "shadow-yellow-500/25",
    solutions: [
      "Interactive Dashboards", 
      "Real-time Reporting", 
      // Use the localized version directly
      locale === 'en-AU' || locale === 'en-NZ' ? "Custom Visualisations" : "Custom Visualizations"
    ],
    href: "/technologies/data-visualization",
    isWide: false
  };
};

export function TechnologiesSection() {
  const params = useParams();
  const currentLocale = params?.locale as string || 'en';
  
  // Add the Data Visualization tech with properly localized capabilities
  const technologies = [
    ...baseTechnologies.slice(0, 5),
    createDataVisualizationTech(currentLocale),
    ...baseTechnologies.slice(5)
  ];
  
  // Force "Visualizations" to be localized properly for debugging
  const test = localizeSpelling("Custom Visualizations", currentLocale);
  console.log(`Locale: ${currentLocale}, Original: "Custom Visualizations", Localized: "${test}"`);
  
  // Localize the technology data
  const localizedTechnologies = technologies.map(tech => {
    // Special handling for data visualization card
    if (tech.name === "Data Visualization") {
      console.log(`Processing Data Visualization card for locale: ${currentLocale}`);
      const localizedSolutions = tech.solutions.map(solution => {
        const localized = localizeSpelling(solution, currentLocale);
        console.log(`  - Solution "${solution}" localized to "${localized}"`);
        return localized;
      });
      
      return {
        ...tech,
        name: localizeSpelling(tech.name, currentLocale),
        description: localizeSpelling(tech.description, currentLocale),
        solutions: localizedSolutions
      };
    }
    
    // Regular handling for other cards
    return {
      ...tech,
      name: localizeSpelling(tech.name, currentLocale),
      description: localizeSpelling(tech.description, currentLocale),
      solutions: tech.solutions.map(solution => localizeSpelling(solution, currentLocale))
    };
  });
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
        duration: 1.0
      }
    }
  };

  const itemVariants = {
    hidden: { 
      opacity: 0, 
      y: 40,
      scale: 0.9
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: { 
        type: "spring", 
        stiffness: 100, 
        damping: 15,
        mass: 0.8,
        duration: 0.8
      } 
    }
  };

  // Create wave offsets for an organic feel
  const getRowOffset = (index: number) => {
    return [0, 0.1, 0.05, 0.15, 0.08, 0.12, 0.03, 0.18][index % 8];
  };

  return (
    <div className="px-4 py-24 bg-slate-50">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-center mb-16 max-w-4xl mx-auto"
        >
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
            {localizeSpelling("Technologies We Work With", currentLocale)}
          </h2>
          <p className="text-slate-600 text-lg md:text-xl leading-relaxed">
            {localizeSpelling("We leverage cutting-edge technologies to deliver innovative solutions that drive business value and keep you ahead of the competition.", currentLocale)}
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-8xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          {localizedTechnologies.map((technology, index) => {
            const IconComponent = technology.icon;
            
            return (
              <motion.div 
                key={technology.name}
                variants={itemVariants}
                custom={index}
                className={`h-full ${technology.isWide ? 'md:col-span-2' : 'md:col-span-1'}`}
                transition={{
                  delay: getRowOffset(index)
                }}
                whileHover={{ 
                  y: -8,
                  scale: 1.02,
                  transition: { 
                    type: "spring", 
                    stiffness: 300, 
                    damping: 20,
                    duration: 0.4
                  }
                }}
              >
                <Link href={`/${currentLocale}${technology.href}`} className="block h-full group">
                  <div className={`rounded-2xl overflow-hidden h-full relative bg-gradient-to-br ${technology.gradient} p-[1px] shadow-lg hover:shadow-xl ${technology.glowColor} transition-all duration-500 group-hover:shadow-2xl`}>
                    
                    {/* Glass morphism background */}
                    <div className="absolute inset-[1px] bg-white/95 backdrop-blur-xl rounded-2xl">
                      {/* Subtle gradient overlay */}
                      <div className={`absolute inset-0 bg-gradient-to-br ${technology.gradient} opacity-5 group-hover:opacity-10 transition-opacity duration-500`}></div>
                      
                      {/* Animated shine effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 group-hover:animate-shimmer opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                    </div>
                    
                    {/* Content */}
                    <div className="relative z-10 p-8 flex flex-col h-full">
                      {/* Icon with gradient background */}
                      <div className={`relative mb-6 w-16 h-16 rounded-2xl bg-gradient-to-br ${technology.gradient} p-3 shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110`}>
                        <IconComponent 
                          className="w-full h-full text-white drop-shadow-sm" 
                          strokeWidth={1.5}
                        />
                        {/* Icon glow effect */}
                        <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${technology.gradient} blur-md opacity-50 group-hover:opacity-75 transition-opacity duration-300 -z-10`}></div>
                      </div>
                      
                      <div className="flex-1 space-y-4">
                        <div>
                          <h3 className="text-slate-900 text-xl font-bold mb-2 group-hover:text-slate-800 transition-colors duration-300">
                            {technology.name}
                          </h3>
                          <p className="text-slate-600 text-base font-medium leading-relaxed">
                            {technology.description}
                          </p>
                        </div>
                        
                        <div className="space-y-3">
                          <h4 className="text-slate-800 text-sm font-semibold uppercase tracking-wider">
                            {localizeSpelling("Capabilities", currentLocale)}
                          </h4>
                          <ul className="space-y-2">
                            {technology.solutions.map((solution) => (
                              <li key={solution} className="text-slate-600 text-sm flex items-start group-hover:text-slate-700 transition-colors duration-300">
                                <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${technology.gradient} mr-3 mt-2 flex-shrink-0`}></div>
                                <span className="leading-relaxed">{solution}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      
                      {/* Subtle arrow indicator */}
                      <div className="flex justify-end mt-6">
                        <div className={`w-8 h-8 rounded-full bg-gradient-to-r ${technology.gradient} flex items-center justify-center shadow-md group-hover:shadow-lg transition-all duration-300 group-hover:scale-110`}>
                          <TrendingUp className="w-4 h-4 text-white" strokeWidth={2} />
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </div>
  );
} 